/**
 * SchoolSchedulerPanel — manages APScheduler jobs + settings + run history.
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2, Clock, Play, Zap, Mail, Award, AlertTriangle, Sparkles } from 'lucide-react';
import api from '@/lib/api';

const JOB_META = {
  weekly_brief:           { label: 'Weekly Executive Brief',  icon: Mail,           desc: 'Mondays 07:00 UTC · compiles Brief + PDF, emails admins' },
  daily_attendance:       { label: 'Daily Attendance Alerts', icon: AlertTriangle,  desc: 'Daily 06:30 UTC · queues attendance-alert guardians' },
  weekly_autoaward:       { label: 'Weekly Auto-Award',       icon: Award,          desc: 'Sundays 22:00 UTC · scans students+teachers for new badges' },
  weekly_hr_digest:       { label: 'Weekly HR Digest',        icon: Zap,            desc: 'Mondays 07:15 UTC · emails HR digest to admins' },
  weekly_bible_popquiz:   { label: 'Weekly Bible Pop Quiz',   icon: Sparkles,       desc: 'Sundays 20:00 UTC · drafts a pop quiz per plan, alerts admins to review' },
  copilot_approval_digest:{ label: 'Copilot Approval Digest', icon: Mail,           desc: 'Mondays 07:30 UTC · emails admins a list of pending Copilot drafts' },
  copilot_morning_briefing:{label: 'Copilot Morning Briefing',icon: AlertTriangle,  desc: 'Daily 06:00 UTC · scans for conflicts / stuck states and alerts admins' },
  copilot_learnings_digest:{label: 'Copilot Weekly Recap',    icon: Award,          desc: 'Fridays 16:00 UTC · emails admins what Copilot accomplished' },
};
const TOGGLE_KEY = {
  weekly_brief: 'weekly_brief_enabled',
  daily_attendance: 'daily_attendance_enabled',
  weekly_autoaward: 'weekly_autoaward_enabled',
  weekly_hr_digest: 'weekly_hr_digest_enabled',
  weekly_bible_popquiz: 'weekly_bible_popquiz_enabled',
  copilot_approval_digest: 'copilot_approval_digest_enabled',
  copilot_morning_briefing: 'copilot_morning_briefing_enabled',
  copilot_learnings_digest: 'copilot_learnings_digest_enabled',
};

export const SchoolScheduler = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: sys, isLoading: sysL } = useQuery({ queryKey: ['sch-sys'], queryFn: () => api.get(`/school/scheduler/system-jobs`).then(r => r.data), refetchInterval: 30000 });
  const { data: settings = {}, isLoading: setL } = useQuery({ queryKey: ['sch-settings', tenantId], queryFn: () => api.get(`/school/tenants/${tenantId}/scheduler/settings`).then(r => r.data) });
  const { data: runs = [] } = useQuery({ queryKey: ['sch-runs', tenantId], queryFn: () => api.get(`/school/tenants/${tenantId}/scheduler/runs`).then(r => r.data), refetchInterval: 20000 });

  const toggleMut = useMutation({
    mutationFn: (patch) => api.put(`/school/tenants/${tenantId}/scheduler/settings`, patch),
    onSuccess: () => { toast.success('Updated'); qc.invalidateQueries({ queryKey: ['sch-settings', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });
  const runMut = useMutation({
    mutationFn: (jobId) => api.post(`/school/tenants/${tenantId}/scheduler/run/${jobId}`),
    onSuccess: (r) => { toast.success(`Ran: ${r.data.last_run?.status} — ${r.data.last_run?.details || ''}`); qc.invalidateQueries({ queryKey: ['sch-runs', tenantId] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed'),
  });

  return (
    <div className="space-y-4" data-testid="scheduler-panel">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2"><Clock className="h-5 w-5" /> Background Scheduler</h2>
        <p className="text-sm text-slate-500">Cron-driven jobs for briefs, HR digests, alerts, and auto-award</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            System status
            {sys?.running
              ? <Badge className="bg-emerald-100 text-emerald-800">RUNNING</Badge>
              : <Badge variant="destructive">STOPPED</Badge>}
          </CardTitle>
          <CardDescription>All times in UTC. Next run times update when the process reloads.</CardDescription>
        </CardHeader>
        <CardContent>
          {sysL ? <Loader2 className="h-4 w-4 animate-spin" /> : (
            <div className="grid md:grid-cols-2 gap-3">
              {(sys?.jobs || []).map(j => {
                const meta = JOB_META[j.id] || { label: j.id, icon: Clock, desc: '' };
                const Icon = meta.icon;
                const enabled = settings[TOGGLE_KEY[j.id]];
                return (
                  <div key={j.id} className="p-3 rounded-lg border bg-slate-50" data-testid={`sch-job-${j.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-indigo-600" />
                        <div>
                          <div className="text-sm font-medium">{meta.label}</div>
                          <div className="text-xs text-slate-500">{meta.desc}</div>
                        </div>
                      </div>
                      <label className="flex items-center gap-1 text-xs text-slate-600" title="Enable for this tenant">
                        <input type="checkbox" checked={!!enabled} onChange={(e) => toggleMut.mutate({ [TOGGLE_KEY[j.id]]: e.target.checked })} disabled={setL || toggleMut.isPending} data-testid={`sch-toggle-${j.id}`} />
                        Enabled
                      </label>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-[11px] text-slate-500">Next: {j.next_run_time ? new Date(j.next_run_time).toLocaleString() : '—'}</div>
                      <Button size="sm" variant="outline" onClick={() => runMut.mutate(j.id)} disabled={runMut.isPending} data-testid={`sch-run-${j.id}`}>
                        {runMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Play className="h-3 w-3 mr-1" /> Run now</>}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-sm">Recent runs</CardTitle></CardHeader>
        <CardContent>
          {runs.length === 0 ? <p className="text-sm text-slate-500 text-center py-4" data-testid="sch-runs-empty">No runs yet.</p> :
            <Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Job</TableHead><TableHead>Status</TableHead><TableHead>Details</TableHead></TableRow></TableHeader>
              <TableBody>
                {runs.slice(0, 30).map(r => (
                  <TableRow key={r.id} data-testid={`sch-run-row-${r.id}`}>
                    <TableCell className="text-xs">{r.ran_at?.slice(0, 19).replace('T', ' ')}</TableCell>
                    <TableCell className="text-xs font-medium">{r.job}</TableCell>
                    <TableCell><Badge className={r.status === 'ok' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}>{r.status}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-600">{r.details || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>}
        </CardContent>
      </Card>
    </div>
  );
};

export default SchoolScheduler;
